/**
 * Voice Memo Metadata
 *
 * Normalizes chat voice memo metadata for send, render, and history flows.
 *
 * Primary responsibilities:
 * - Build stable voice memo metadata for user messages
 * - Read persisted voice memo metadata from message rows
 * - Keep transcript text from rendering twice in voice messages
 *
 * Dependencies:
 * - Supabase Json types
 */

import type { Json } from '@/supabase/types'
import type {
  ChatVoiceMemoMetadata,
  ChatVoiceMemoTranscriptSegment,
  ChatVoiceMemoTranscriptionStatus
} from '@/types/voice-memo'

type JsonRecord = Record<string, Json>

const DEFAULT_SEGMENT_DURATION_SECONDS = 0.1
const MAX_WAVEFORM_SAMPLE_COUNT = 48
const MIN_MEANINGFUL_WAVEFORM_SAMPLE = 0.08
const MIN_MEANINGFUL_WAVEFORM_PEAK = 0.16
const MIN_MEANINGFUL_WAVEFORM_ACTIVE_SAMPLES = 3
export const VOICE_MEMO_TRANSCRIPT_PREFIX = 'Transcript of Voice Note:'

// --------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

// --------------

function readString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

// --------------

function readNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

// --------------

function clampWaveformSample(value: number) {
  return Math.min(1, Math.max(0, value))
}

// --------------

function readStatus(value: unknown): ChatVoiceMemoTranscriptionStatus {
  return value === 'pending' || value === 'failed' || value === 'transcribed'
    ? value
    : 'transcribed'
}

// --------------

function normalizeSegment(value: unknown): ChatVoiceMemoTranscriptSegment | null {
  if (!isRecord(value)) {
    return null
  }

  const text = readString(value.text)
  if (!text) {
    return null
  }

  const rawStart = readNumber(value.startSecond) ?? readNumber(value.start) ?? 0
  const rawEnd =
    readNumber(value.endSecond) ??
    readNumber(value.end) ??
    rawStart + DEFAULT_SEGMENT_DURATION_SECONDS

  return {
    text,
    startSecond: Math.max(0, rawStart),
    endSecond: Math.max(rawStart, rawEnd)
  }
}

// --------------

export function createTranscriptSegments(
  transcript: string,
  durationSeconds?: number | null
): ChatVoiceMemoTranscriptSegment[] {
  const text = transcript.trim()
  if (!text) {
    return []
  }

  return [
    {
      text,
      startSecond: 0,
      endSecond:
        typeof durationSeconds === 'number' && Number.isFinite(durationSeconds)
          ? Math.max(durationSeconds, DEFAULT_SEGMENT_DURATION_SECONDS)
          : DEFAULT_SEGMENT_DURATION_SECONDS
    }
  ]
}

// --------------

export function normalizeTranscriptionSegments(
  value: unknown,
  fallbackTranscript = '',
  durationSeconds?: number | null
): ChatVoiceMemoTranscriptSegment[] {
  const segments = Array.isArray(value)
    ? value.flatMap((item) => {
        const segment = normalizeSegment(item)
        return segment ? [segment] : []
      })
    : []

  return segments.length > 0
    ? segments
    : createTranscriptSegments(fallbackTranscript, durationSeconds)
}

// --------------

export function buildChatVoiceMemoMetadata(input: {
  audioPath?: string | null
  audioUrl: string
  contentType: string
  createdAt?: string
  durationSeconds?: number | null
  segments?: ChatVoiceMemoTranscriptSegment[]
  sizeBytes: number
  transcript: string
  transcriptionStatus?: ChatVoiceMemoTranscriptionStatus
  waveformSamples?: number[]
}): ChatVoiceMemoMetadata {
  const transcript = input.transcript.trim()
  const durationSeconds =
    typeof input.durationSeconds === 'number' && Number.isFinite(input.durationSeconds)
      ? input.durationSeconds
      : null

  return {
    audioPath: input.audioPath?.trim() || null,
    audioUrl: input.audioUrl.trim(),
    contentType: input.contentType,
    durationSeconds,
    kind: 'voice_memo',
    segments:
      input.segments && input.segments.length > 0
        ? input.segments
        : createTranscriptSegments(transcript, durationSeconds),
    sizeBytes: input.sizeBytes,
    transcript,
    transcriptionStatus: input.transcriptionStatus ?? 'transcribed',
    waveformSamples: normalizeVoiceMemoWaveformSamples(input.waveformSamples)
  }
}

// --------------

export const buildVoiceMemoSegments = createTranscriptSegments

// --------------

export function normalizeVoiceMemoWaveformSamples(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .flatMap((item) => {
      const sample = readNumber(item)
      return sample === null ? [] : [clampWaveformSample(sample)]
    })
    .slice(-MAX_WAVEFORM_SAMPLE_COUNT)
}

// --------------

export function hasMeaningfulVoiceMemoWaveform(value: unknown) {
  const samples = normalizeVoiceMemoWaveformSamples(value)

  if (samples.length < 4) {
    return false
  }

  const peak = Math.max(...samples)
  const activeSampleCount = samples.filter(
    (sample) => sample >= MIN_MEANINGFUL_WAVEFORM_SAMPLE
  ).length

  return (
    peak >= MIN_MEANINGFUL_WAVEFORM_PEAK &&
    activeSampleCount >= MIN_MEANINGFUL_WAVEFORM_ACTIVE_SAMPLES
  )
}

// --------------

export function updateChatVoiceMemoTranscript(
  voiceMemo: ChatVoiceMemoMetadata,
  transcript: string
): ChatVoiceMemoMetadata {
  const cleanTranscript = transcript.trim()

  return {
    ...voiceMemo,
    segments: createTranscriptSegments(cleanTranscript, voiceMemo.durationSeconds),
    transcript: cleanTranscript,
    transcriptionStatus: cleanTranscript ? 'transcribed' : 'failed'
  }
}

// --------------

export function formatVoiceMemoTranscriptForModel(
  voiceMemo: ChatVoiceMemoMetadata | string
) {
  const transcript =
    typeof voiceMemo === 'string' ? voiceMemo.trim() : voiceMemo.transcript.trim()

  return transcript
}

// --------------

export function readVoiceMemoMetadata(metadata: unknown): ChatVoiceMemoMetadata | null {
  if (!isRecord(metadata)) {
    return null
  }

  const rawVoiceMemo = metadata.voiceMemo
  if (!isRecord(rawVoiceMemo)) {
    return null
  }

  const audioUrl = readString(rawVoiceMemo.audioUrl)
  const contentType =
    readString(rawVoiceMemo.contentType) ?? readString(rawVoiceMemo.mimeType)
  const transcript = readString(rawVoiceMemo.transcript) ?? ''
  const sizeBytes =
    readNumber(rawVoiceMemo.sizeBytes) ?? readNumber(rawVoiceMemo.size)

  if (!audioUrl || !contentType || sizeBytes === null) {
    return null
  }

  const durationSeconds = readNumber(rawVoiceMemo.durationSeconds)

  return {
    audioPath:
      readString(rawVoiceMemo.audioPath) ?? readString(rawVoiceMemo.storagePath),
    audioUrl,
    contentType,
    durationSeconds,
    kind: 'voice_memo',
    segments: normalizeTranscriptionSegments(
      rawVoiceMemo.segments,
      transcript,
      durationSeconds
    ),
    sizeBytes,
    transcript,
    transcriptionStatus: readStatus(rawVoiceMemo.transcriptionStatus),
    waveformSamples: normalizeVoiceMemoWaveformSamples(rawVoiceMemo.waveformSamples)
  }
}

// --------------

export function mergeVoiceMemoMetadata(
  metadata: Json | null,
  voiceMemo?: ChatVoiceMemoMetadata | null
): Json | null {
  if (!voiceMemo) {
    return metadata
  }

  const baseMetadata =
    isRecord(metadata) && !Array.isArray(metadata)
      ? (metadata as JsonRecord)
      : {}

  return {
    ...baseMetadata,
    voiceMemo: voiceMemo as unknown as Json
  } as Json
}

// --------------

export function getVoiceMemoVisibleText(
  content: string,
  voiceMemo: ChatVoiceMemoMetadata | null
) {
  const transcript = voiceMemo?.transcript.trim()
  const trimmedContent = content.trim()

  if (!transcript || !trimmedContent) {
    return content
  }

  const modelTranscript = formatVoiceMemoTranscriptForModel(transcript)
  const legacyModelTranscript = `${VOICE_MEMO_TRANSCRIPT_PREFIX}\n${transcript}`

  if (trimmedContent === legacyModelTranscript) {
    return ''
  }

  if (trimmedContent === modelTranscript) {
    return ''
  }

  if (trimmedContent.endsWith(legacyModelTranscript)) {
    return trimmedContent.slice(0, -legacyModelTranscript.length).trim()
  }

  if (trimmedContent.endsWith(modelTranscript)) {
    return trimmedContent.slice(0, -modelTranscript.length).trim()
  }

  if (trimmedContent === transcript) {
    return ''
  }

  if (trimmedContent.endsWith(transcript)) {
    return trimmedContent.slice(0, -transcript.length).trim()
  }

  return content
}
